import React, { useState, useEffect } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonLoading, IonToast } from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Autocomplete,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Add, Delete, Visibility, CheckCircle, Pending, Warning, Inventory, LocalShipping } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getVendors } from '../../../api/vendor/vendor';
import { getProducts } from '../../../api/products/product';
import { baseURL, config, requestID } from '../../../common/utils';
import { createPurchaseOrder, getPurchaseOrders } from '../../../api/purchase-orders/purchase-orders';
import axios from 'axios';
import { formatToNaira } from '../../../common/utils_t';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  '&.pending': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  '&.approved': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  '&.rejected': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  '&.fulfilled': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

interface Product {
  id: string;
  productName: string;
  name: string;
  sku: string;
  current_stock: number;
  min_stock: number;
  total_quantity_left: number;
}

interface Vendor {
  id: string;
  user_id: string;
  name: string;
  firstName: string;
  lastName: string;
  contact_email: string;
  lead_time: number; // in days
}

interface PurchaseOrderItem {
  id: string;
  product_id: string;
  product_name: string;
  productName?: string;
  quantity: number;
  unit_price: number;
  vendor_id: string;
}

interface PurchaseOrder {
  id: string;
  vendor_id: string;
  vendor_name: string;
  items: PurchaseOrderItem[];
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'fulfilled'|"pending';";
  request_id?: string;
  request_date: string;
  expected_delivery_date?: string;
  notes?: string;
  requested_by: string;
}

const PurchaseOrderRequestPage_PO: React.FC = () => {
  // State for products and vendors (would come from your existing lists)
  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  
  // State for new purchase order
  const [newOrder, setNewOrder] = useState<Omit<PurchaseOrder, 'id'>>({
    vendor_id: '',
    vendor_name: '',
    items: [],
    status: 'draft',
    request_date: new Date().toISOString(),
    requested_by: 'Warehouse Manager' // Would come from auth in real app
  });
  
  const [currentItem, setCurrentItem] = useState<{
    product_id: string;
    product_name: string;
    productName: string;
    quantity: number;
    unit_price: number;
    vendor_id: string;
  }>({
    product_id: '',
    product_name: '',
    productName: '',
    quantity: 1,
    unit_price: 0,
    vendor_id: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentViewOrder, setCurrentViewOrder] = useState<PurchaseOrder | null>(null);

  // Load initial data
  useEffect(() => {
    fetchProducts();
    fetchVendors();
    fetchPurchaseOrders();
  }, []);

  // Mock API calls
  const fetchProducts = async () => {
    setLoading(true);
    try {

    const res = await getProducts()
    console.log('product',res)
      setProducts(res.data.data);
    } catch (error) {
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await getVendors()
      console.log(res)
      setVendors(res.data.data);
    } catch (error) {
      showError('Failed to load vendors');
    }
  };

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const response = await getPurchaseOrders()
      console.log(response.data)
      setPurchaseOrders(response.data.data);

    } catch (error) {
      showError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };



  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const openViewDialog = (order: PurchaseOrder) => {
    setCurrentViewOrder(order);
    setViewDialogOpen(true);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Vendor Purchase Orders</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Purchase Order Requests
              </Typography>
            
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    {/* <TableCell>Vendor</TableCell> */}
                    <TableCell>Items</TableCell>
                    <TableCell>Request Date</TableCell>
                    <TableCell>Expected Delivery</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {loading ? <CircularProgress size={24} /> : 'No purchase orders found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrders.map((order,i) => (
                      <TableRow key={i}>
                        <TableCell>{order?.request_id.toUpperCase()}</TableCell>
                        {/* <TableCell>{order.vendor_name}</TableCell> */}
                        <TableCell>
                          <Chip 
                            label={`${order.items.length} items`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(order.request_date)}</TableCell>
                        <TableCell>
                          {order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            label={order.status} 
                            className={order.status}
                            icon={
                              order.status === 'approved' ? <CheckCircle fontSize="small" /> :
                              order.status === 'rejected' ? <Warning fontSize="small" /> :
                              <Pending fontSize="small" />
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={() => openViewDialog(order)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>

   

        {/* View Purchase Order Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          {currentViewOrder && (
            <>
              <DialogTitle>Purchase Order {currentViewOrder.id}</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    <strong>Vendor:</strong> {currentViewOrder.vendor_name}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Status:</strong> 
                    <StatusChip 
                      label={currentViewOrder.status} 
                      className={currentViewOrder.status}
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Requested by:</strong> {currentViewOrder.requested_by}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Request Date:</strong> {formatDate(currentViewOrder.request_date)}
                  </Typography>
                  {currentViewOrder.expected_delivery_date && (
                    <Typography variant="subtitle1">
                      <strong>Expected Delivery:</strong> {formatDate(currentViewOrder.expected_delivery_date)}
                    </Typography>
                  )}
                  {currentViewOrder.notes && (
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      <strong>Notes:</strong> {currentViewOrder.notes}
                    </Typography>
                  )}
                </Box>

                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                      
                        <TableCell>Vendor</TableCell>
                        <TableCell>Approved Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                   <TableBody>
  {currentViewOrder.items.map(item => (
    <TableRow key={item.id}>
      <TableCell>{item.product_name}</TableCell>
      <TableCell align="right">{item.quantity}</TableCell>
   
      
      <TableCell>
        {vendors.find(v => v.id == item.vendor_id)?.name || 'New Vendor'}
      </TableCell>
      
      <TableCell>
        <TextField
          fullWidth
          type="number"
          label="Approved Quantity"

          value={item.quantity}
          onChange={(e) => {
            // handleQtyChange(item.id, parseInt(e.target.value) || 0);
          }}
          // inputProps={{ min: 1 }}
        />
      </TableCell>
    </TableRow>
  ))}
</TableBody>

                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="subtitle1">
                    <strong>Total Items:</strong> {currentViewOrder.items.length}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Total Quantity:</strong> {currentViewOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                  </Typography>
                  {/* <Typography variant="h6">
                    <strong>Order Total:</strong> 
                    {formatToNaira(currentViewOrder.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2))}
                  </Typography> */}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                {currentViewOrder.status === 'pending' && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                  >
                    Approve Order
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        <IonLoading isOpen={loading} message={'Processing...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
        />
      </IonContent>
    </IonPage>
  );
};

export default PurchaseOrderRequestPage_PO;